import React, { FC, memo, useLayoutEffect, useState } from 'react';
import { User } from '../user.types';
import cls from './UserCardInfoBlock.module.scss';
import Icon from '../../widget/Icon';
import { useAppDispatch, useAppSelector } from '../../../hooks/defaultHooks';
import { openModal } from '../../../store/slices/modalSlice';
import { getIsAtLeastAdmin } from '../../../selectors/selectors';
import { closeOffcanvas, openOffcanvas } from '../../../store/slices/offcanvasSlice';
import { setLogsFieldSearch } from '../../../store/slices/logsFilterSlice';
import { useNavigate } from 'react-router-dom';
import routes from '../../../routes';
import Button from '../../../UIComponents/Button';
import { GrDatabase } from 'react-icons/gr';
import { MdLockReset, MdOutlineEdit } from 'react-icons/md';

type UserCardInfoBlockProps = {
  user: User;
};

const UserCardInfoBlock: FC<UserCardInfoBlockProps> = memo((props) => {
  const { user } = props;
  const [name, setName] = useState('Unknown Beaver');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isAtLeastAdmin = useAppSelector(getIsAtLeastAdmin);
  const changeUserDataHandler = () =>
    dispatch(openOffcanvas({ type: 'updateUserOffcanvas', extra: { id: user?.id } }));
  const changeUserPasswordHandler = () =>
    dispatch(
      openModal({ type: 'changeUserPassword', extra: { email: user?.email, id: user?.id } }),
    );
  const redirectToLogsPage = () => {
    dispatch(closeOffcanvas());
    // the logs page filters by an account id: the one the log names as object or subject
    dispatch(setLogsFieldSearch({ key: 'objectId', value: user?.id ?? '' }));
    dispatch(setLogsFieldSearch({ key: 'objectEmail', value: user?.email ?? '' }));
    navigate(routes.pages.logsPage());
  };
  useLayoutEffect(() => {
    if (user.firstName && user.secondName) {
      setName(`${user.firstName} ${user.secondName}`);
    } else if (user.firstName && !user.secondName) {
      setName(`${user.firstName} Beaver`);
    } else if (!user.firstName && user.secondName) {
      setName(`Unknown ${user.secondName}`);
    } else {
      setName('Unknown Beaver');
    }
  }, [user.firstName, user.secondName]);
  return (
    <div className={cls.UserInfo}>
      <div className={cls.User}>
        <Icon email={user.email} name={name} />
        <div className={cls.Info}>
          <div className={cls.Property}>
            <span className={cls.FieldName}>First Name</span>
            <span className={cls.FieldValue}>
              {!user?.firstName ? 'Not specified' : user?.firstName}
            </span>
          </div>
          <div className={cls.Property}>
            <span className={cls.FieldName}>Last Name</span>
            <span className={cls.FieldValue}>
              {!user?.secondName ? 'Not specified' : user?.secondName}
            </span>
          </div>
          <div className={cls.Property}>
            <span className={cls.FieldName}>Email</span>
            <a href={`mailto:${user?.email}`} className={`${cls.FieldValue} ${cls.FieldLink}`}>
              {user.email}
            </a>
          </div>
          <div className={cls.Property}>
            <span className={cls.FieldName}>Phone Number</span>
            <span className={cls.FieldValue}>{!user?.phone ? 'Not specified' : user?.phone}</span>
          </div>
        </div>
      </div>
      <div className={cls.Buttons}>
        <Button
          className={cls.Button}
          text={'Edit'}
          Icon={<MdOutlineEdit />}
          clickHandler={changeUserDataHandler}
        />
        <Button
          className={cls.Button}
          text={'Reset Password'}
          Icon={<MdLockReset />}
          clickHandler={changeUserPasswordHandler}
        />
        {isAtLeastAdmin && (
          <div className="link-logs text-center">
            <Button
              className={cls.Button}
              text={'View Logs'}
              Icon={<GrDatabase />}
              clickHandler={redirectToLogsPage}
            />
          </div>
        )}
      </div>
    </div>
  );
});

export default UserCardInfoBlock;
